import { useEffect, useMemo } from "react"
import { useSearchParams } from "react-router-dom"
import { PageHeader } from "@/components/ui/page-header"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { useToast } from "@/components/ui/use-toast"
import { useOrganizationsStore } from "@/stores/organizationsStore"
import { ReplyInbox } from "@/features/outreach"
import { INBOX_STATUSES, SOURCE_LABEL, parseInboxQuery } from "@/features/outreach/lib/inbox"
import type { InboxStatus } from "@/features/outreach"

const STATUS_LABEL: Record<InboxStatus, string> = {
  pending: "Open",
  completed: "Closed",
  all: "All",
}

/**
 * /outreach/inbox?org=<id>&source=<source>&status=<pending|completed|all>
 *
 * The outreach inbox on its own page. The clinic's weekly report email links
 * here (source=outreach_not_my_patient&status=all). `org` wins over the
 * selected organization: when the user belongs to it, the selection switches
 * to it; otherwise the API answers 403 and ReplyInbox says so.
 */
export default function OutreachInboxPage() {
  const { toast } = useToast()
  const [searchParams, setSearchParams] = useSearchParams()
  const query = useMemo(() => parseInboxQuery(searchParams), [searchParams])
  const { selectedOrganizationId, organizations, setSelectedOrganizationId } = useOrganizationsStore()

  const orgId = query.org || selectedOrganizationId
  const org = organizations.find((o) => o.id === orgId)

  // Follow the link's organization in the rest of the portal too, once we know the user has it.
  useEffect(() => {
    if (!query.org || query.org === selectedOrganizationId) return
    const target = organizations.find((o) => o.id === query.org)
    if (!target) return
    setSelectedOrganizationId(target.id)
    toast({ title: "Organization switched", description: `Now showing ${target.name}.` })
  }, [query.org, selectedOrganizationId, organizations, setSelectedOrganizationId, toast])

  const setParam = (key: string, value: string | null) => {
    const next = new URLSearchParams(searchParams)
    if (value) next.set(key, value)
    else next.delete(key)
    setSearchParams(next, { replace: true })
  }

  const title = query.source ? SOURCE_LABEL[query.source] || "Outreach inbox" : "Outreach inbox"

  return (
    <div className="mx-auto max-w-5xl min-w-0 space-y-4 p-4 md:space-y-6 md:p-8">
      <PageHeader
        title={title}
        description={org ? org.name : undefined}
        actions={
          <>
            {INBOX_STATUSES.map((s) => (
              <Button
                key={s}
                size="sm"
                variant={query.status === s ? "default" : "outline"}
                onClick={() => setParam("status", s === "pending" ? null : s)}
              >
                {STATUS_LABEL[s]}
              </Button>
            ))}
            {query.source && (
              <Button size="sm" variant="ghost" onClick={() => setParam("source", null)}>
                All kinds
              </Button>
            )}
          </>
        }
      />

      {orgId ? (
        <ReplyInbox key={orgId} orgId={orgId} source={query.source} status={query.status} />
      ) : (
        <Card>
          <CardContent className="py-6 text-sm text-muted-foreground">Select an organization to see its inbox.</CardContent>
        </Card>
      )}
    </div>
  )
}
